/**
 * Runtime configuration. Every environment-dependent value is read here once
 * so no component ever touches `import.meta.env` directly.
 */
export type DataMode = 'mock' | 'api';

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  if (value === 'true' || value === true) return true;
  if (value === 'false' || value === false) return false;
  return fallback;
}

const env = import.meta.env as Record<string, string | boolean | undefined>;
const runtimeOrigin =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'http://localhost:3000';
const runtimeWsOrigin = runtimeOrigin.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');

const rawMode = str(env.VITE_DATA_MODE, 'api');

export const config = {
  /** API is the full-stack default; VITE_DATA_MODE=mock remains fully offline. */
  dataMode: (rawMode === 'api' ? 'api' : 'mock') as DataMode,
  apiBaseUrl: str(env.VITE_API_BASE_URL, `${runtimeOrigin}/api/v1`).replace(/\/+$/, ''),
  wsBaseUrl: str(env.VITE_WS_BASE_URL, `${runtimeWsOrigin}/api/v1`).replace(/\/+$/, ''),
  restaurantSlug: str(env.VITE_RESTAURANT_SLUG, 'dr-calorie'),
  defaultTableLabel: str(env.VITE_DEFAULT_TABLE_LABEL, '12'),
  liveVoiceEnabled: bool(env.VITE_ENABLE_LIVE_VOICE, true),
  mediaBaseUrl: str(env.VITE_MEDIA_BASE_URL, ''),
  isDev: Boolean(env.DEV),
} as const;

export const isMockMode = () => config.dataMode === 'mock';

/**
 * Resolves a demo media path against the configured CDN prefix.
 * Absolute URLs pass through untouched.
 */
export function mediaUrl(path: string): string {
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  const base = config.mediaBaseUrl.replace(/\/+$/, '');
  const clean = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${clean}` : clean;
}
